/**
 * Turn timing on assistant messages — "Worked for 4m 12s" / "You stopped after 38s".
 *
 * The server puts it on the AI SDK message's `metadata` (start chunk:
 * `startedAt`; finish/abort: `finishedAt` + `aborted`) and it is persisted with
 * the message, so it is there for history too.  Mirrors the API's
 * `packages/agent-core/src/ai-chat/ai-chat.turn-timing.ts` — keep the two in step.
 *
 * Reading it: assistant-ui's message conversion keeps only `metadata.custom`
 * (and a few built-ins) from the converter output, so these flat fields are NOT
 * on `useMessage().metadata`.  They are read from the underlying AI SDK
 * message(s) instead, via `getExternalStoreMessages` — see `useTurnTiming`.
 *
 * Messages saved before this existed have no metadata; every field is optional.
 */
import { useMemo } from 'react';
import type { UIMessage } from 'ai';
import { getExternalStoreMessages, useAuiState } from '@assistant-ui/react';

export interface TurnTimingMetadata {
  /** ISO time the server received the turn's request. */
  startedAt?: string;
  /** ISO time the turn ended: the stream finished, or the abort landed. */
  finishedAt?: string;
  /** True when the turn was stopped (Stop / disconnect) rather than finishing. */
  aborted?: boolean;
}

function timingOf(message: UIMessage | undefined): TurnTimingMetadata | undefined {
  const m = message?.metadata;
  return m && typeof m === 'object' ? (m as TurnTimingMetadata) : undefined;
}

/**
 * Combine the timing of the AI SDK messages behind one rendered message.
 * Normally there is exactly one; consecutive assistant messages are joined into
 * one by assistant-ui, in which case the span runs from the first start to the
 * last end.  Undefined when none of them carries timing (older messages).
 */
export function combineTurnTiming(messages: readonly UIMessage[]): TurnTimingMetadata | undefined {
  let startedAt: string | undefined;
  let end: TurnTimingMetadata | undefined;
  for (const msg of messages) {
    const t = timingOf(msg);
    if (!t) continue;
    if (!startedAt && typeof t.startedAt === 'string') startedAt = t.startedAt;
    if (typeof t.finishedAt === 'string') end = t;
  }
  if (!startedAt && !end) return undefined;
  return {
    ...(startedAt ? { startedAt } : {}),
    ...(end ? { finishedAt: end.finishedAt, aborted: end.aborted === true } : {}),
  };
}

/**
 * Turn timing of the message in the current `MessagePrimitive` scope.
 * Undefined for user messages and for assistant messages saved before timing
 * existed.  While a turn streams, `startedAt` is set and `finishedAt` is not.
 */
export function useTurnTiming(): TurnTimingMetadata | undefined {
  // The inputs array is cached by the converter per message, so this selector
  // is referentially stable between unrelated renders.
  const inputs = useAuiState((s) => getExternalStoreMessages<UIMessage>(s.message));
  return useMemo(() => combineTurnTiming(inputs), [inputs]);
}

/**
 * The message's real time, for "is this message recent?" checks.  NOT
 * `useMessage().createdAt`: the AI SDK converter stamps that with the time of
 * conversion, so every message loaded from history looks brand new.  Prefers
 * the end of the turn; undefined when unknown (treat as old).
 */
export function turnTimestamp(timing: TurnTimingMetadata | undefined): Date | undefined {
  const iso = timing?.finishedAt ?? timing?.startedAt;
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d;
}
